import React, { useEffect, useRef, useState } from 'react';
import PlayingCard from './PlayingCard';
import { useCardActivate } from '../utils/cardActivate';
import {
  HAND_CARD_W,
  HAND_CARD_H,
  HAND_STACK_STEP,
  HAND_SLOT_COUNT,
  handAxisForVisualSeat,
  handStackSpan,
  lockedSouthStep,
} from '../utils/handLayout';

/**
 * Opponent arc fan (N/E/W). South stays flat.
 *
 * - Fixed radius (same curvature whether hand is full or short)
 * - Uniform spacing along the arc (like south stack step) — cards re-center as played
 * - Arc pivot sits beyond the table edge (math only); container wraps cards only
 */
const FAN_HALF_DEG = 45;
const FAN_HALF_RAD = (FAN_HALF_DEG * Math.PI) / 180;
/** Full spades hand — used only to lock arc radius so it does not shrink as cards leave */
const ARC_RADIUS_HAND = 13;
/**
 * Extra +X for east AND west (visual right). Applied in layout so it always shows
 * even if seat CSS transforms are overridden by media queries.
 */
const SIDE_HAND_SHIFT_RIGHT_PX = 26;
/** Extra +X for east only (west is already dialed in). */
const EAST_EXTRA_SHIFT_RIGHT_PX = 12;

/**
 * Shared arc geometry for one opponent hand.
 * Returns per-card styles + container size that only wraps the cards.
 */
function opponentArcHand(total, visualSeat, cardW, cardH, step) {
  const n = Math.max(total, 1);
  const mid = (n - 1) / 2;
  const sinHalf = Math.sin(FAN_HALF_RAD) || 0.01;
  const cosHalf = Math.cos(FAN_HALF_RAD) || 0.01;

  // Radius locked to full-hand chord so arc size matches N/E/W and stays stable
  const fullHalfChord = ((ARC_RADIUS_HAND - 1) / 2) * step;
  const radius = Math.max(
    Math.max(cardW, cardH) * 1.55,
    fullHalfChord / sinHalf,
  );

  const cardHalf = Math.max(cardW, cardH) * 0.52;
  // Pivot past felt edge (math only — not in layout box)
  let pivotBeyond = Math.max(
    radius * 0.42,
    radius * cosHalf - cardHalf * 0.35,
  );
  if (visualSeat === 'east' || visualSeat === 'west') {
    // Keep E/W near rim; further right shift applied after via SIDE_HAND_SHIFT_RIGHT_PX
    pivotBeyond = Math.max(pivotBeyond, radius * 0.62);
  }

  /*
   * Uniform spacing (south-style): chord offset from cluster center = (idx - mid) * step.
   * θ = s / R  → fixed step along the arc; remaining cards stay centered as n drops.
   * (Old code used idx/mid * ±45° which stretched the whole fan for any n.)
   */
  const raw = [];
  for (let idx = 0; idx < n; idx += 1) {
    const s = (idx - mid) * step; // px along arc from center of cluster
    const theta = s / radius; // radians
    const thetaDeg = (theta * 180) / Math.PI;
    const along = radius * Math.sin(theta);
    const toward = radius * Math.cos(theta);

    let cx;
    let cy;
    let rot;
    switch (visualSeat) {
      case 'north':
        cx = along;
        cy = toward;
        rot = 180 - thetaDeg;
        break;
      case 'west':
        cx = toward;
        cy = along;
        rot = -90 + thetaDeg;
        break;
      case 'east':
        cx = -toward;
        cy = along;
        rot = 90 - thetaDeg;
        break;
      default:
        cx = along;
        cy = 0;
        rot = 0;
    }
    raw.push({
      idx,
      left: cx - cardW / 2,
      top: cy - cardH / 2,
      rot,
    });
  }

  let shiftX = 0;
  let shiftY = 0;
  const pad = 4;
  if (visualSeat === 'north') {
    shiftX = -Math.min(...raw.map((c) => c.left)) + pad;
    shiftY = -pivotBeyond;
  } else if (visualSeat === 'west') {
    shiftX = -pivotBeyond;
    shiftY = -Math.min(...raw.map((c) => c.top)) + pad;
  } else if (visualSeat === 'east') {
    shiftX = pivotBeyond;
    shiftY = -Math.min(...raw.map((c) => c.top)) + pad;
  }

  // Side seats: shared right shift; east gets a bit more toward the right rim
  if (visualSeat === 'west' || visualSeat === 'east') {
    shiftX += SIDE_HAND_SHIFT_RIGHT_PX;
  }
  if (visualSeat === 'east') {
    shiftX += EAST_EXTRA_SHIFT_RIGHT_PX;
  }

  const placed = raw.map((c) => ({
    idx: c.idx,
    left: Math.round(c.left + shiftX),
    top: Math.round(c.top + shiftY),
    rot: c.rot,
  }));

  const boxPad = 6;
  const minL = Math.min(...placed.map((c) => c.left));
  const minT = Math.min(...placed.map((c) => c.top));
  const maxR = Math.max(...placed.map((c) => c.left + cardW));
  const maxB = Math.max(...placed.map((c) => c.top + cardH));

  const nudgeX = minL < 0 ? -minL + 2 : 0;
  const nudgeY = minT < 0 ? -minT + 2 : 0;

  const styles = placed.map((c) => ({
    left: `${c.left + nudgeX}px`,
    top: `${c.top + nudgeY}px`,
    transform: `rotate(${c.rot}deg)`,
    transformOrigin: 'center center',
    zIndex: c.idx + 10,
  }));

  const boxW = Math.ceil(maxR - minL + nudgeX + boxPad);
  const boxH = Math.ceil(maxB - minT + nudgeY + boxPad);

  return {
    styles,
    box: { w: Math.max(boxW, cardW + 8), h: Math.max(boxH, cardH + 8) },
  };
}

/**
 * Flat horizontal stack — south / your hand.
 * step is locked to full-hand span across available width; n cards re-center.
 */
function flatSouthStyle(idx, step, startOffset = 0) {
  return {
    left: `${startOffset + idx * step}px`,
    top: '0',
    zIndex: idx + 10,
  };
}

function stackDimsFlat(layoutSlots, cardW, cardH, step, boxWidth = null) {
  const span = handStackSpan(layoutSlots, {
    cardW, cardH, step, axis: 'row',
  });
  return {
    width: `${boxWidth != null ? boxWidth : span}px`,
    height: `${cardH}px`,
  };
}

/**
 * Hand strip — face-up for your cards; face-down stubs for opponents.
 * South / isYou: flat. N/E/W: arc with uniform spacing, re-centers as cards leave.
 */
function SeatHand({
  visualSeat,
  isYou,
  isPeek = false,
  faceUp,
  cards,
  count,
  reserveSlots = 0,
  reserveVisible = false,
  canRaise = false,
  canPlay = false,
  onRaiseCard,
  onPlayCard,
  cardW = HAND_CARD_W,
  cardH = HAND_CARD_H,
  stackStep = HAND_STACK_STEP,
}) {
  const handleCard = useCardActivate({
    onRaise: canRaise ? onRaiseCard : null,
    onPlay: canPlay ? onPlayCard : null,
  });

  const zoneRef = useRef(null);
  const [availWidth, setAvailWidth] = useState(0);

  const hasCards = cards && cards.length > 0;
  const showFaceUp = faceUp && hasCards;
  const visibleCount = hasCards ? cards.length : (count > 0 ? count : 0);
  const layoutSlots = visibleCount > 0
    ? visibleCount
    : Math.max(reserveSlots, 0);

  const useArc = !isYou
    && visualSeat !== 'south'
    && (visualSeat === 'north' || visualSeat === 'east' || visualSeat === 'west');
  const useFlatSouth = !useArc;

  // Measure available width for south / own hand so step can lock to full table span.
  // Important: do NOT use the seat's content width — it shrink-wraps to the hand and
  // creates a chicken-and-egg with step (hand stays bunched). Use the felt / grid row.
  useEffect(() => {
    if (!useFlatSouth) return undefined;
    const el = zoneRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const measureWidth = () => {
      const seat = el.closest('.spades-seat');
      const felt = el.closest('.spades-felt');
      const floatDock = el.closest('.spades-player-hand-float');

      let w = 0;
      // Floated hand strip (full felt width via left/right insets)
      if (floatDock && floatDock.clientWidth > 0) {
        w = floatDock.clientWidth;
      }
      // South grid row spans the felt — use felt content box when seat is content-sized
      if (felt && felt.clientWidth > 0) {
        const cs = window.getComputedStyle(felt);
        const pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
        w = Math.max(w, felt.clientWidth - pad);
      }
      // If seat is stretched to 100% of the south area, its clientWidth is reliable
      if (seat && seat.clientWidth > 0) {
        // Only trust seat when it is at least as wide as a mid-size strip
        // (avoids locking onto shrink-wrapped content width)
        const seatW = seat.clientWidth;
        if (!felt || seatW >= felt.clientWidth * 0.85) {
          w = Math.max(w, seatW);
        }
      }
      if (!w) {
        w = el.parentElement?.clientWidth || el.clientWidth || 0;
      }
      // leave a little edge padding so cards aren't flush to felt rim
      setAvailWidth(Math.max(0, w - 16));
    };

    measureWidth();
    const ro = new ResizeObserver(measureWidth);
    ro.observe(el);
    const seat = el.closest('.spades-seat');
    const felt = el.closest('.spades-felt');
    const floatDock = el.closest('.spades-player-hand-float');
    if (seat) ro.observe(seat);
    if (felt) ro.observe(felt);
    if (floatDock) ro.observe(floatDock);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => ro.disconnect();
  }, [useFlatSouth, cardW]);

  if (layoutSlots === 0) {
    return null;
  }

  const axis = handAxisForVisualSeat(visualSeat);

  let styles;
  let stackStyle;
  if (useArc) {
    const arc = opponentArcHand(layoutSlots, visualSeat, cardW, cardH, stackStep);
    styles = arc.styles;
    stackStyle = {
      width: `${arc.box.w}px`,
      height: `${arc.box.h}px`,
    };
  } else {
    // Lock step as if a full hand spans the table; re-center as cards leave.
    // Fallback width must not use tight stackStep span — that re-bunches before measure.
    const tightFallback = cardW + (HAND_SLOT_COUNT - 1) * stackStep;
    const wideFallback = typeof window !== 'undefined'
      ? Math.max(tightFallback, Math.min(window.innerWidth * 0.85, 920) - 24)
      : tightFallback;
    const widthForStep = availWidth > 0 ? availWidth : wideFallback;
    const step = lockedSouthStep(widthForStep, cardW, HAND_SLOT_COUNT);
    const clusterSpan = handStackSpan(layoutSlots, {
      cardW, cardH, step, axis: 'row',
    });
    const boxW = availWidth > 0 ? availWidth : Math.max(clusterSpan, widthForStep);
    const startOffset = Math.max(0, (boxW - clusterSpan) / 2);
    styles = Array.from({ length: layoutSlots }, (_, idx) => (
      flatSouthStyle(idx, step, startOffset)
    ));
    stackStyle = stackDimsFlat(layoutSlots, cardW, cardH, step, boxW);
  }

  const placeholderCount = Math.max(0, layoutSlots - visibleCount);

  const renderPlaceholders = (startIdx) => (
    Array.from({ length: placeholderCount }, (_, i) => {
      const idx = startIdx + i;
      return (
        <div
          key={`placeholder-${idx}`}
          className={[
            'spades-hand-card',
            'spades-hand-card--placeholder',
            reserveVisible ? 'spades-hand-card--reserve-visible' : '',
          ].filter(Boolean).join(' ')}
          style={{
            ...(styles[idx] || {}),
            width: `${cardW}px`,
            height: `${cardH}px`,
          }}
          aria-hidden="true"
        />
      );
    })
  );

  return (
    <div
      ref={zoneRef}
      className={[
        'spades-hand-zone',
        `spades-hand-zone--${visualSeat}`,
        isYou ? 'spades-hand-zone--yours' : 'spades-hand-zone--opponent',
        isPeek ? 'spades-hand-zone--peek' : '',
        showFaceUp ? 'spades-hand-zone--face-up' : 'spades-hand-zone--face-down',
        layoutSlots > visibleCount ? 'spades-hand-zone--reserved' : '',
        reserveVisible && visibleCount === 0 ? 'spades-hand-zone--reserve-visible' : '',
        useArc ? 'spades-hand-zone--arc' : 'spades-hand-zone--flat',
        useFlatSouth ? 'spades-hand-zone--spread' : '',
      ].filter(Boolean).join(' ')}
      aria-label={isYou ? 'Your hand' : 'Player hand'}
      aria-hidden={visibleCount === 0 && reserveSlots > 0}
    >
      <div
        className={[
          'spades-hand-cards',
          `spades-hand-cards--${axis}`,
          'spades-hand-cards--stacked',
          useArc ? 'spades-hand-cards--fan' : '',
        ].filter(Boolean).join(' ')}
        style={stackStyle}
      >
        {placeholderCount > 0 && renderPlaceholders(visibleCount)}
        {showFaceUp
          ? cards.map((item, idx) => (
            <PlayingCard
              key={item.id}
              card={item}
              raised={!!item.raised}
              width={cardW}
              height={cardH}
              className="spades-hand-card"
              style={styles[idx] || {}}
              onClick={(canRaise || canPlay) ? () => handleCard(item.id) : undefined}
              onDoubleClick={canPlay ? (e) => { e.preventDefault(); onPlayCard?.(item.id); } : undefined}
            />
          ))
          : hasCards
            ? cards.map((item, idx) => (
              <PlayingCard
                key={item.id}
                faceDown
                raised={!!item.raised}
                width={cardW}
                height={cardH}
                className="spades-hand-card spades-hand-card--stub"
                style={styles[idx] || {}}
                onClick={canRaise ? () => handleCard(item.id) : undefined}
              />
            ))
            : Array.from({ length: visibleCount }, (_, i) => (
              <PlayingCard
                key={`stub-${i}`}
                faceDown
                width={cardW}
                height={cardH}
                className="spades-hand-card spades-hand-card--stub"
                style={styles[i] || {}}
              />
            ))}
      </div>
    </div>
  );
}

export default SeatHand;
